/**
 * Outlets by Time — the profit model.
 *
 * Kept out of the component so the arithmetic can be read (and reasoned about)
 * on its own. The API returns sales facts only; everything here is derived, so
 * dragging a cost percentage re-renders instantly with no round trip.
 *
 * THE MODEL (agreed with Chris, 2026-08-03)
 *
 *   Cost of Goods  — VARIABLE. Scales with each hour's takings.
 *   Staffing       — FIXED FOR THE DAY. A quiet Tuesday still pays its staff.
 *   Utilities      — likewise fixed.
 *   Maintenance    — likewise fixed.
 *   Profit margin  — never entered; always the remainder of the four.
 *
 * The three fixed percentages form a pot struck at SEASON level:
 *
 *   pot          = (staffing% + utilities% + maintenance%) / 100 * seasonNet
 *   fixedPerDay  = pot / seasonTradingDays
 *   fixedPerSlot = fixedPerDay / (hours that outlet actually traded that day)
 *
 * Because SUM(dayShare) over a season is exactly the number of trading days,
 * SUM(fixed) is exactly the pot, so:
 *
 *   SUM(month profit) = seasonNet * (1 - cogs%) - pot = seasonNet * margin%
 *
 * The outlet's overall margin therefore lands precisely on the remainder the
 * exec team set. Only the DISTRIBUTION across months, days and hours moves —
 * which is what makes a quiet hour show red while the outlet is still fine for
 * the year.
 */

// ---------------------------------------------------------------------------
//  API shapes
// ---------------------------------------------------------------------------

export interface ApiSlot {
  month: number; // fiscal month, 0 = March
  category: string;
  location: string;
  slot: number; // slot start, minutes from midnight (570 = 09:30)
  net: number;
  gross: number;
  cost: number;
  costed_net: number;
  items: number;
  transactions: number;
  day_share: number; // SUM over the month's days of 1 / (slots that day)
  day_count: number; // how many days in the month traded in this hour
}

export interface ApiMonth {
  month: number;
  category: string;
  location: string;
  trading_days: number;
}

export interface ApiCostAdjust {
  month: number;
  location: string;
  cost_adjust: number;
}

export interface CostModelRow {
  category: string;
  location: string | null; // null = the category default
  cogs_pct: number;
  staffing_pct: number;
  utilities_pct: number;
  maintenance_pct: number;
  management_pct: number;
  /** headcount on shift; with a rate, replaces staffing_pct entirely */
  staff_count: number | null;
  staff_cost_per_hour: number | null;
  /** true when both of the above are set and positive */
  staff_hourly: boolean;
  /** null when staffing is hourly — the margin is then an outcome, not an input */
  margin_pct: number | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface ApiSeason {
  fiscalYear: number;
  fiscalYearLabel: string;
  years: number[];
  outlets: { category: string; location: string }[];
  slots: ApiSlot[];
  months: ApiMonth[];
  cost_adjustments: ApiCostAdjust[];
  cost_model: CostModelRow[];
  last_refresh: string | null;
}

export interface ApiDayRow {
  date: string;
  category: string;
  location: string;
  slot: number;
  net: number;
  gross: number;
  cost: number;
  costed_net: number;
  items: number;
  transactions: number;
  slots: number; // hours this outlet traded that day
}

/** Which cost of goods to use. Retail only has real cost; tea rooms are always modelled. */
export type CogsSource = "modelled" | "actual";

/**
 * Where staffing cost comes from:
 *   model   — the cost model (Staffing %, or headcount x hourly rate)
 *   iplicit — real payroll from finance-nymr, by category by month, apportioned
 *             across a category's outlets pro-rata by net. Falls back to the
 *             model for any month finance has no run for yet.
 */
export type StaffSource = "model" | "iplicit";

/** finance-nymr payroll: real staff cost, category x fiscal month. */
export interface StaffCostRow {
  category: string;
  month: number; // fiscal month, 0 = March
  people: number;
  employer_cost: number;
}

// ---------------------------------------------------------------------------
//  Fiscal calendar. Fiscal month 0 = March ... 11 = February.
// ---------------------------------------------------------------------------

export const FISCAL_MONTHS = [
  "Mar", "Apr", "May", "Jun", "Jul", "Aug",
  "Sep", "Oct", "Nov", "Dec", "Jan", "Feb",
];

/** Fiscal month index -> calendar year, given the fiscal year (labelled by end year). */
export function calendarYearFor(fiscalMonth: number, fiscalYear: number): number {
  // 0..9 = Mar..Dec of the START year; 10..11 = Jan..Feb of the END year
  return fiscalMonth <= 9 ? fiscalYear - 1 : fiscalYear;
}

export function fiscalMonthLabel(fiscalMonth: number, fiscalYear: number): string {
  return `${FISCAL_MONTHS[fiscalMonth]} ${calendarYearFor(fiscalMonth, fiscalYear)}`;
}

// ---------------------------------------------------------------------------
//  Hour slots
// ---------------------------------------------------------------------------

/** The trading window the exec view labels by default: 09:30 -> 17:30. */
export const DEFAULT_FIRST_SLOT = 570; // 09:30
export const DEFAULT_LAST_SLOT = 990; // 16:30-17:30 is the last full labelled slot

export function slotLabel(slot: number): string {
  const hh = Math.floor(slot / 60) % 24;
  const mm = slot % 60;
  const eh = Math.floor((slot + 60) / 60) % 24;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(hh)}:${pad(mm)}-${pad(eh)}:${pad(mm)}`;
}

export function slotShortLabel(slot: number): string {
  const hh = Math.floor(slot / 60) % 24;
  const mm = slot % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/**
 * Fold a slot into the display window. Anything before 09:30 or after 17:30 is
 * bucketed into an edge column rather than dropped — the tea rooms open before
 * 09:00 and Shopify sells overnight, and silently binning that trade would make
 * the hour totals disagree with the month total.
 */
export const EDGE_EARLY = -1;
export const EDGE_LATE = -2;

export function foldSlot(slot: number, allHours: boolean): number {
  if (allHours) return slot;
  if (slot < DEFAULT_FIRST_SLOT) return EDGE_EARLY;
  if (slot > DEFAULT_LAST_SLOT) return EDGE_LATE;
  return slot;
}

export function foldedSlotLabel(slot: number): string {
  if (slot === EDGE_EARLY) return "Before 09:30";
  if (slot === EDGE_LATE) return "After 17:30";
  return slotLabel(slot);
}

// ---------------------------------------------------------------------------
//  Cost model resolution
// ---------------------------------------------------------------------------

export interface ResolvedCosts {
  cogs: number;
  staffing: number;
  utilities: number;
  maintenance: number;
  management: number;
  /** the remainder margin; only meaningful while staffing is a percentage */
  margin: number;
  staffCount: number | null;
  staffCostPerHour: number | null;
  /** when true, staffing is headcount x rate x hours traded, not staffing% */
  staffHourly: boolean;
  /** true when this outlet has its own override rather than the category default */
  isOverride: boolean;
}

export function resolveCosts(
  model: CostModelRow[],
  category: string,
  location: string
): ResolvedCosts {
  const override = model.find((m) => m.category === category && m.location === location);
  const fallback = model.find((m) => m.category === category && !m.location);
  const row = override ?? fallback;
  const cogs = row?.cogs_pct ?? 0;
  const staffing = row?.staffing_pct ?? 0;
  const utilities = row?.utilities_pct ?? 0;
  const maintenance = row?.maintenance_pct ?? 0;
  const management = row?.management_pct ?? 0;
  const staffCount = row?.staff_count ?? null;
  const staffCostPerHour = row?.staff_cost_per_hour ?? null;
  return {
    cogs,
    staffing,
    utilities,
    maintenance,
    management,
    margin: 100 - cogs - staffing - utilities - maintenance - management,
    staffCount,
    staffCostPerHour,
    staffHourly: Boolean(row?.staff_hourly),
    isOverride: Boolean(override),
  };
}

/** MERAC only carries cost for retail stock — tea rooms are always modelled. */
export function categoryHasActualCost(category: string): boolean {
  return category === "Retail";
}

// ---------------------------------------------------------------------------
//  Derived per-outlet model
// ---------------------------------------------------------------------------

export interface Bucket {
  net: number;
  cogs: number;
  fixed: number;
  profit: number;
  /** share of net that had a real MERAC cost behind it (actual mode only) */
  costedNet: number;
  items: number;
  transactions: number;
  /** hours actually worked in this bucket — the multiplier for an hourly wage */
  dayCount: number;
}

export interface SlotBucket extends Bucket {
  slot: number;
}

export interface MonthModel extends Bucket {
  month: number;
  tradingDays: number;
  slots: SlotBucket[];
}

export interface OutletModel extends Bucket {
  category: string;
  location: string;
  costs: ResolvedCosts;
  tradingDays: number;
  fixedPerDay: number;
  /** headcount x rate, charged per trading hour; 0 when staffing is a % */
  staffPerHour: number;
  /** total hours the outlet traded across the season */
  tradingHours: number;
  /** real payroll staff cost applied from finance (iplicit source), season total */
  iplicitStaffTotal: number;
  /** true when at least one month used the iplicit payroll figure */
  usesIplicitStaff: boolean;
  months: MonthModel[];
  /** season-level slot roll-up, for the "all months" summary */
  slots: SlotBucket[];
  /** % of net with a real cost — only meaningful when using actual cost */
  coveragePct: number | null;
}

function emptyBucket(): Bucket {
  return { net: 0, cogs: 0, fixed: 0, profit: 0, costedNet: 0, items: 0, transactions: 0, dayCount: 0 };
}

function addInto(target: Bucket, src: Bucket): void {
  target.net += src.net;
  target.cogs += src.cogs;
  target.fixed += src.fixed;
  target.profit += src.profit;
  target.costedNet += src.costedNet;
  target.items += src.items;
  target.transactions += src.transactions;
  target.dayCount += src.dayCount;
}

/**
 * Cost of goods for one bucket.
 *
 * "actual" is deliberately BLENDED: real cost where the line had one, and the
 * modelled % on the uncosted remainder. Pure actual would report a ~70% margin
 * on Shopify, where only ~31% of net is costed — a number that looks like
 * profit and isn't.
 */
export function cogsFor(
  net: number,
  actualCost: number,
  costedNet: number,
  cogsPct: number,
  source: CogsSource,
  hasActual: boolean
): number {
  if (source === "modelled" || !hasActual) return (net * cogsPct) / 100;
  const uncosted = Math.max(0, net - costedNet);
  return actualCost + (uncosted * cogsPct) / 100;
}

export function buildOutletModels(
  data: ApiSeason,
  source: CogsSource,
  staffSource: StaffSource = "model",
  staffCosts: StaffCostRow[] = [],
  staffBackfill = false
): OutletModel[] {
  const out: OutletModel[] = [];

  // For iplicit staffing: a category's monthly payroll total is apportioned
  // across its outlets pro-rata by net, so we need each category-month's net
  // across ALL outlets first.
  const catMonthNet = new Map<string, number>();
  for (const s of data.slots) {
    const k = `${s.category}|${s.month}`;
    catMonthNet.set(k, (catMonthNet.get(k) ?? 0) + s.net);
  }
  const staffMap = new Map<string, number>();
  for (const r of staffCosts) staffMap.set(`${r.category}|${r.month}`, r.employer_cost);

  // Back-fill: in the absence of a full year of payroll, carry the LAST month
  // finance has run into the earlier months (payroll is roughly constant month
  // to month, so this is a fair stand-in). Applied per category to every month
  // present in the season that has no real figure yet.
  if (staffBackfill && staffCosts.length) {
    const latestByCat = new Map<string, { month: number; value: number }>();
    for (const r of staffCosts) {
      const cur = latestByCat.get(r.category);
      if (!cur || r.month > cur.month) latestByCat.set(r.category, { month: r.month, value: r.employer_cost });
    }
    const monthsPresent = new Set(data.months.map((m) => m.month));
    for (const [cat, latest] of latestByCat) {
      for (const m of monthsPresent) {
        const k = `${cat}|${m}`;
        if (!staffMap.has(k)) staffMap.set(k, latest.value);
      }
    }
  }

  for (const outlet of data.outlets) {
    const { category, location } = outlet;
    const costs = resolveCosts(data.cost_model, category, location);
    const hasActual = categoryHasActualCost(category);

    const slotRows = data.slots.filter((s) => s.category === category && s.location === location);
    const monthRows = data.months.filter((m) => m.category === category && m.location === location);

    const seasonNet = slotRows.reduce((a, s) => a + s.net, 0);
    const tradingDays = monthRows.reduce((a, m) => a + m.trading_days, 0);

    // Real headcount x hourly rate, charged per hour traded (model hourly mode).
    const staffPerHour = costs.staffHourly
      ? (costs.staffCount ?? 0) * (costs.staffCostPerHour ?? 0)
      : 0;

    // "Other" fixed pot = utilities + maintenance + management, ALWAYS. Staffing
    // is handled separately per mode below so it is never double-counted.
    const otherPot = (seasonNet * (costs.utilities + costs.maintenance + costs.management)) / 100;
    const otherPerDay = tradingDays > 0 ? otherPot / tradingDays : 0;
    // Percentage-staffing pot (model %, non-hourly), spread per trading day.
    const staffPctPerDay =
      tradingDays > 0 ? (seasonNet * costs.staffing) / 100 / tradingDays : 0;

    // Kept for the KPIs / fixed-cost popup: total fixed per day at the model %.
    const fixedPerDay = otherPerDay + (costs.staffHourly ? 0 : staffPctPerDay);

    // Item overrides arrive as a per-month cash delta; spread across that
    // month's hours pro-rata by net.
    const monthNet = new Map<number, number>();
    for (const s of slotRows) monthNet.set(s.month, (monthNet.get(s.month) ?? 0) + s.net);
    const adjust = new Map<number, number>();
    for (const a of data.cost_adjustments) {
      if (a.location === location) adjust.set(a.month, a.cost_adjust);
    }

    let iplicitStaffTotal = 0; // sum of real staff cost actually applied
    let iplicitMonths = 0;

    const months: MonthModel[] = [];
    for (const mr of monthRows) {
      const mSlots = slotRows.filter((s) => s.month === mr.month);
      const mNet = monthNet.get(mr.month) ?? 0;
      const mAdjust = adjust.get(mr.month) ?? 0;

      // Iplicit staffing for this outlet-month: the category's payroll total for
      // the month, apportioned to this outlet by its share of category net.
      // Only when the pill is on AND finance has a run for that month — else the
      // month falls back to the model staffing so the tab never goes blank.
      const catStaff = staffMap.get(`${category}|${mr.month}`);
      const useIplicit = staffSource === "iplicit" && catStaff != null;
      let outletMonthStaff = 0;
      if (useIplicit) {
        const cmn = catMonthNet.get(`${category}|${mr.month}`) ?? 0;
        outletMonthStaff = cmn > 0 ? (catStaff as number) * (mNet / cmn) : 0;
        iplicitStaffTotal += outletMonthStaff;
        iplicitMonths += 1;
      }

      const slots: SlotBucket[] = mSlots.map((s) => {
        const share = mNet > 0 ? s.net / mNet : 0;
        const actualCost = s.cost + mAdjust * share;
        const cogs = cogsFor(s.net, actualCost, s.costed_net, costs.cogs, source, hasActual);
        // Other fixed (util/maint/mgmt) always spreads by day_share. Staffing:
        // iplicit month total by day_share fraction, else hourly x hours, else
        // the % pot by day_share.
        const staff = useIplicit
          ? (mr.trading_days > 0 ? outletMonthStaff * (s.day_share / mr.trading_days) : 0)
          : costs.staffHourly
          ? staffPerHour * s.day_count
          : staffPctPerDay * s.day_share;
        const fixed = otherPerDay * s.day_share + staff;
        return {
          slot: s.slot,
          net: s.net,
          cogs,
          fixed,
          profit: s.net - cogs - fixed,
          costedNet: s.costed_net,
          items: s.items,
          transactions: s.transactions,
          dayCount: s.day_count,
        };
      });

      const agg: Bucket = emptyBucket();
      for (const s of slots) addInto(agg, s);
      months.push({ ...agg, month: mr.month, tradingDays: mr.trading_days, slots });
    }
    months.sort((a, b) => a.month - b.month);

    // season slot roll-up (all months combined, by hour slot)
    const bySlot = new Map<number, SlotBucket>();
    for (const m of months) {
      for (const s of m.slots) {
        const cur = bySlot.get(s.slot) ?? { ...emptyBucket(), slot: s.slot };
        addInto(cur, s);
        bySlot.set(s.slot, cur);
      }
    }
    const slots = Array.from(bySlot.values()).sort((a, b) => a.slot - b.slot);

    const total: Bucket = emptyBucket();
    for (const m of months) addInto(total, m);

    out.push({
      ...total,
      category,
      location,
      costs,
      tradingDays,
      fixedPerDay,
      staffPerHour,
      tradingHours: slotRows.reduce((a, s) => a + s.day_count, 0),
      iplicitStaffTotal,
      usesIplicitStaff: iplicitMonths > 0,
      months,
      slots,
      coveragePct: hasActual && total.net > 0 ? (total.costedNet / total.net) * 100 : null,
    });
  }

  // Retail outlets first, then tea rooms; alphabetical within each.
  out.sort(
    (a, b) =>
      a.category.localeCompare(b.category) || a.location.localeCompare(b.location)
  );
  return out;
}

// ---------------------------------------------------------------------------
//  Day drill-down
// ---------------------------------------------------------------------------

export interface DayModel extends Bucket {
  date: string;
  slots: SlotBucket[];
}

export function buildDayModels(
  rows: ApiDayRow[],
  outlet: OutletModel,
  source: CogsSource,
  monthAdjust: number,
  monthNet: number
): DayModel[] {
  const hasActual = categoryHasActualCost(outlet.category);
  const mine = rows.filter(
    (r) => r.category === outlet.category && r.location === outlet.location
  );

  const byDate = new Map<string, SlotBucket[]>();
  for (const r of mine) {
    const share = monthNet > 0 ? r.net / monthNet : 0;
    const actualCost = r.cost + monthAdjust * share;
    const cogs = cogsFor(r.net, actualCost, r.costed_net, outlet.costs.cogs, source, hasActual);
    // Fixed cost is fixed for the DAY, split across the hours that day traded,
    // plus the wage bill for this one hour when staffing is costed hourly.
    const fixed = (r.slots > 0 ? outlet.fixedPerDay / r.slots : 0) + outlet.staffPerHour;
    const bucket: SlotBucket = {
      slot: r.slot,
      net: r.net,
      cogs,
      fixed,
      profit: r.net - cogs - fixed,
      costedNet: r.costed_net,
      items: r.items,
      transactions: r.transactions,
      dayCount: 1, // a day-grain row is exactly one hour worked
    };
    const list = byDate.get(r.date) ?? [];
    list.push(bucket);
    byDate.set(r.date, list);
  }

  const days: DayModel[] = [];
  for (const [date, slots] of byDate) {
    slots.sort((a, b) => a.slot - b.slot);
    const agg = emptyBucket();
    for (const s of slots) addInto(agg, s);
    days.push({ ...agg, date, slots });
  }
  days.sort((a, b) => a.date.localeCompare(b.date));
  return days;
}

// ---------------------------------------------------------------------------
//  Slot folding for display
// ---------------------------------------------------------------------------

export function foldSlots(slots: SlotBucket[], allHours: boolean): SlotBucket[] {
  const by = new Map<number, SlotBucket>();
  for (const s of slots) {
    const key = foldSlot(s.slot, allHours);
    const cur = by.get(key) ?? { ...emptyBucket(), slot: key };
    addInto(cur, s);
    by.set(key, cur);
  }
  // edge buckets sit outside the clock, so sort them to the ends
  return Array.from(by.values()).sort((a, b) => {
    const rank = (n: number) => (n === EDGE_EARLY ? -Infinity : n === EDGE_LATE ? Infinity : n);
    return rank(a.slot) - rank(b.slot);
  });
}

// ---------------------------------------------------------------------------
//  Outlet standing — the flag on each outlet tab
//
//  An outlet averaging under this much profit per trading month is treated as
//  loss-making, per Chris: a shop scraping a few hundred pounds is not really
//  paying its way even though the arithmetic is positive.
// ---------------------------------------------------------------------------

export const LOSS_THRESHOLD_PER_MONTH = 500;

export type OutletStanding = "best" | "worst" | "loss" | null;

/**
 * Flag every outlet as best / worst / loss.
 *
 * "Loss" takes precedence over both others: if the best performer is itself
 * under the threshold, calling it Best in green would be actively misleading —
 * better to say the whole estate is struggling. Best and Worst are only
 * meaningful when there is something to compare, so a single outlet gets
 * nothing.
 *
 * Ranked on profit rather than margin: a 40% margin on £900 is not a better
 * outlet than 20% on £90,000, and this flag is about which one to worry about.
 */
export function outletStandings(outlets: OutletModel[]): Map<string, OutletStanding> {
  const out = new Map<string, OutletStanding>();
  if (!outlets.length) return out;

  const key = (o: OutletModel) => `${o.category}|${o.location}`;
  const perMonth = (o: OutletModel) => o.profit / Math.max(1, o.months.length);

  for (const o of outlets) {
    out.set(key(o), perMonth(o) < LOSS_THRESHOLD_PER_MONTH ? "loss" : null);
  }
  if (outlets.length < 2) return out;

  const ranked = [...outlets].sort((a, b) => b.profit - a.profit);
  const best = ranked[0];
  const worst = ranked[ranked.length - 1];

  if (out.get(key(best)) !== "loss") out.set(key(best), "best");
  if (out.get(key(worst)) !== "loss") out.set(key(worst), "worst");
  return out;
}

export function standingLabel(s: OutletStanding): string | null {
  return s === "best" ? "Best" : s === "worst" ? "Worst" : s === "loss" ? "Loss" : null;
}

// ---------------------------------------------------------------------------
//  Opening-hours / staffing what-if
//
//  Answers "what if this outlet opened later, shut earlier, or ran a different
//  rota?" against the season's real hour-by-hour trade.
//
//  ASSUMPTIONS, stated plainly because they drive the answer:
//
//   1. Closing an hour loses ALL of that hour's takings. No displacement — in
//      reality some of those customers buy earlier or later instead, so the
//      revenue loss shown is the WORST case and the true position is somewhat
//      better. Treat a marginal result as "close to the line", not proven.
//   2. Cost of goods scales with the sales actually taken.
//   3. Utilities and maintenance are genuinely fixed: the same cash whether
//      you open eight hours or six, so they do NOT shrink with the window.
//      Only staffing and cost of goods respond.
//   4. Staffing is charged per hour open, at headcount x hourly rate. Where
//      the outlet has no hourly rate set, one is implied from its staffing %
//      so the model still works: impliedRate = staffing£ / hours traded.
// ---------------------------------------------------------------------------

export interface ScenarioInput {
  /** slot start minutes; hours before this are closed */
  openSlot: number;
  /** slot start minutes; hours after this are closed */
  closeSlot: number;
  /** slot -> headcount for that hour; missing = the outlet default */
  staffBySlot: Record<number, number>;
  /** £/hour per member of staff */
  ratePerHour: number;
  /**
   * When true, sales from the now-closed hours are NOT lost — they fold into
   * the trading total (customers assumed to buy in the open hours instead). No
   * revenue is lost; only staffing shrinks with the shorter day.
   */
  foldInOutOfHours: boolean;
}

export interface ScenarioResult {
  net: number;
  cogs: number;
  staffing: number;
  otherFixed: number;
  profit: number;
  marginPct: number | null;
  hours: number;
  /** net of the closed hours that was folded back in (foldIn mode only) */
  foldInSales: number;
  /** the same figures with the outlet's real hours and default rota */
  baseline: {
    net: number;
    cogs: number;
    staffing: number;
    otherFixed: number;
    profit: number;
    marginPct: number | null;
    hours: number;
  };
}

/**
 * The hourly rate to model with: the outlet's own if set, otherwise implied
 * from its staffing percentage so the sliders still mean something before
 * anyone has typed a wage in.
 */
export function modelRate(outlet: OutletModel): number {
  if (outlet.costs.staffHourly && outlet.costs.staffCostPerHour) {
    return outlet.costs.staffCostPerHour;
  }
  const staffingPot = (outlet.net * outlet.costs.staffing) / 100;
  const perHead = outlet.costs.staffCount && outlet.costs.staffCount > 0 ? outlet.costs.staffCount : 1;
  return outlet.tradingHours > 0 ? staffingPot / outlet.tradingHours / perHead : 0;
}

export function defaultStaffCount(outlet: OutletModel): number {
  return outlet.costs.staffCount && outlet.costs.staffCount > 0 ? outlet.costs.staffCount : 1;
}

export function runScenario(outlet: OutletModel, input: ScenarioInput): ScenarioResult {
  const baseStaff = defaultStaffCount(outlet);

  // Utilities + maintenance + management. Staffing is modelled hour by hour
  // below, so it must not also sit inside the fixed pot.
  const otherFixedPct = outlet.costs.utilities + outlet.costs.maintenance + outlet.costs.management;
  const otherFixed = (outlet.net * otherFixedPct) / 100;

  let net = 0, cogs = 0, staffing = 0, hours = 0, foldInSales = 0;
  let bNet = 0, bCogs = 0, bStaffing = 0, bHours = 0;

  for (const s of outlet.slots) {
    // Baseline — every hour the outlet really traded, at the default rota.
    bNet += s.net;
    bCogs += s.cogs;
    bStaffing += baseStaff * input.ratePerHour * s.dayCount;
    bHours += s.dayCount;

    const shut = s.slot < input.openSlot || s.slot > input.closeSlot;
    if (shut) {
      // Fold-in: keep the sales (and their cost of goods) but no staffing —
      // the day is shorter, so the wage bill falls while revenue holds.
      if (input.foldInOutOfHours) {
        net += s.net;
        cogs += s.cogs;
        foldInSales += s.net;
      }
      continue;
    }

    const staff = input.staffBySlot[s.slot] ?? baseStaff;
    net += s.net;
    cogs += s.cogs;
    staffing += staff * input.ratePerHour * s.dayCount;
    hours += s.dayCount;
  }

  const profit = net - cogs - staffing - otherFixed;
  const bProfit = bNet - bCogs - bStaffing - otherFixed;

  return {
    net, cogs, staffing, otherFixed, profit,
    marginPct: net > 0 ? (profit / net) * 100 : null,
    hours,
    foldInSales,
    baseline: {
      net: bNet, cogs: bCogs, staffing: bStaffing, otherFixed, profit: bProfit,
      marginPct: bNet > 0 ? (bProfit / bNet) * 100 : null,
      hours: bHours,
    },
  };
}

// ---------------------------------------------------------------------------
//  Opening-times optimiser
//
//  "Given a staffing cap and minimum profit thresholds, which days and months
//  are worth opening?" Run per outlet, month by month, against the season's
//  real day-by-day trade.
//
//  THE DECISION IS MARGINAL, and that is the whole point:
//   - Closing a day or a month saves STAFFING and COST OF GOODS only.
//   - Utilities, maintenance and management are annual commitments — the rent
//     does not stop for a closed January — so they are incurred whether open or
//     shut, and they do NOT enter the open/close test.
//   - "Profit to open" therefore = sales - cost of goods - staffing (staffing
//     at the Max Paid Staff headcount x the outlet's hourly rate x hours open).
//     A day/month clears the bar only if that marginal figure meets it.
//   - The uplift is the wage (and goods) saved on days/months that were losing
//     money at the door — never a pretence that fixed overhead disappears.
// ---------------------------------------------------------------------------

export interface OptimiseInput {
  maxStaff: number;
  ratePerHour: number;
  minPerDay: number;
  minPerMonth: number;
}

export interface OptimisedMonth {
  month: number;
  open: boolean;              // false = whole month failed the per-month test
  daysOpen: number;
  daysClosed: number;
  sales: number;             // open days only
  cogs: number;
  staffing: number;
  fixed: number;             // full month fixed overhead — incurred regardless
  profit: number;            // sales - cogs - staffing - fixed
  marginPct: number | null;
  /** dates (ISO) closed while the month stays open — for the calendar view */
  closedDates: string[];
}

export interface OptimiseResult {
  months: OptimisedMonth[];
  year: { sales: number; cogs: number; staffing: number; fixed: number; profit: number; marginPct: number | null };
  monthsClosed: number;
  daysClosed: number;
  ratePerHour: number;
}

/**
 * Optimise one outlet's opening pattern.
 * `dayRowsByMonth` is the day-grain data for every month (fetched from the
 * days endpoint). `monthNetByMonth` / `costAdjustByMonth` mirror what the main
 * model uses so cost of goods matches the tab.
 */
// exported for the panel/pdf
export function optimiseOutlet(
  outlet: OutletModel,
  dayRowsByMonth: Record<number, ApiDayRow[]>,
  monthNetByMonth: Record<number, number>,
  costAdjustByMonth: Record<number, number>,
  source: CogsSource,
  input: OptimiseInput
): OptimiseResult {
  const hasActual = categoryHasActualCost(outlet.category);
  // Fixed overhead (utilities + maintenance + management) per trading day —
  // incurred regardless of the open/close decision.
  const otherPerDay =
    outlet.tradingDays > 0
      ? (outlet.net * (outlet.costs.utilities + outlet.costs.maintenance + outlet.costs.management)) /
        100 /
        outlet.tradingDays
      : 0;

  const months: OptimisedMonth[] = [];
  let monthsClosed = 0;
  let daysClosed = 0;

  for (const mm of outlet.months) {
    const rows = (dayRowsByMonth[mm.month] ?? []).filter(
      (r) => r.category === outlet.category && r.location === outlet.location
    );
    const mNet = monthNetByMonth[mm.month] ?? mm.net;
    const mAdjust = costAdjustByMonth[mm.month] ?? 0;

    // group day rows by date
    const byDate = new Map<string, ApiDayRow[]>();
    for (const r of rows) {
      const list = byDate.get(r.date) ?? [];
      list.push(r);
      byDate.set(r.date, list);
    }

    let openSales = 0, openCogs = 0, openStaffing = 0, daysOpen = 0, closedInMonth = 0;
    const closedDates: string[] = [];
    const allDates: string[] = [];

    for (const [date, slots] of byDate) {
      allDates.push(date);
      const hoursOpen = slots[0]?.slots ?? slots.length; // hours the outlet traded that day
      let daySales = 0, dayCogs = 0;
      for (const s of slots) {
        const share = mNet > 0 ? s.net / mNet : 0;
        const actualCost = s.cost + mAdjust * share;
        daySales += s.net;
        dayCogs += cogsFor(s.net, actualCost, s.costed_net, outlet.costs.cogs, source, hasActual);
      }
      const dayStaffing = input.maxStaff * input.ratePerHour * hoursOpen;
      const dayContribution = daySales - dayCogs - dayStaffing;
      // Marginal test: worth opening this day?
      if (dayContribution >= input.minPerDay) {
        openSales += daySales;
        openCogs += dayCogs;
        openStaffing += dayStaffing;
        daysOpen += 1;
      } else {
        closedInMonth += 1;
        closedDates.push(date);
      }
    }
    closedDates.sort();

    const monthFixed = otherPerDay * mm.tradingDays; // incurred whether open or not
    const profit = openSales - openCogs - openStaffing - monthFixed;

    // Per-month test: if even the best open-days pattern can't clear the bar,
    // close the whole month (its fixed overhead still lands in the year).
    if (profit < input.minPerMonth) {
      monthsClosed += 1;
      daysClosed += daysOpen + closedInMonth;
      months.push({
        month: mm.month, open: false,
        daysOpen: 0, daysClosed: daysOpen + closedInMonth,
        sales: 0, cogs: 0, staffing: 0, fixed: monthFixed,
        profit: -monthFixed, marginPct: null,
        closedDates: allDates.sort(), // whole month shut
      });
      continue;
    }

    daysClosed += closedInMonth;
    months.push({
      month: mm.month, open: true,
      daysOpen, daysClosed: closedInMonth,
      sales: openSales, cogs: openCogs, staffing: openStaffing, fixed: monthFixed,
      profit,
      marginPct: openSales > 0 ? (profit / openSales) * 100 : null,
      closedDates,
    });
  }

  const year = months.reduce(
    (a, m) => ({
      sales: a.sales + m.sales,
      cogs: a.cogs + m.cogs,
      staffing: a.staffing + m.staffing,
      fixed: a.fixed + m.fixed,
      profit: a.profit + m.profit,
    }),
    { sales: 0, cogs: 0, staffing: 0, fixed: 0, profit: 0 }
  );

  return {
    months,
    year: { ...year, marginPct: year.sales > 0 ? (year.profit / year.sales) * 100 : null },
    monthsClosed,
    daysClosed,
    ratePerHour: input.ratePerHour,
  };
}

// ---------------------------------------------------------------------------
//  Lean Staffing optimiser
//
//  "Given the current rostered pattern, where is the shop over-staffed for the
//  trade it actually does?" Takes the season's real hour-by-hour transaction
//  loading and the outlet's rostered staff pattern, and:
//
//   1. STAFFING TO DEMAND. For every hour whose transactions-per-hour fall below
//      a threshold (the slider), the headcount is cut PROPORTIONALLY to how far
//      below the line it is — a shop doing a fifth of the threshold keeps a
//      fifth of the staff — never below one person while the door is open, and
//      never above the current roster.
//   2. TRIM THE EDGES. The quiet opening/closing hours are then closed one at a
//      time (open later / close earlier — the same marginal test the opening-
//      times optimiser uses: shut an hour only when its lean wage exceeds the
//      gross margin it earns), guarded so total sales given up never exceed a
//      small % of the outlet's takings.
//
//  COST RULE. The rate applied is the rostering feed's EMPLOYED-ONLY £/staff-
//  hour (roster £cost / roster hours). A roster made entirely of volunteers has
//  a £0 rate, so every "saving" here is honestly £0 — you can send volunteers
//  home but it does not move the P&L. Hours are still shown so the labour
//  reduction is visible even when its cash value is nil.
//
//  Reconciliation: baseline staff cost = baselineHeadcount x rate x SUM(hours)
//  = roster £cost exactly (when the roster basis is used), so "saving vs the
//  current roster" is measured against the real rostered spend.
// ---------------------------------------------------------------------------

export interface LeanStaffingInput {
  /** transactions/hour below which an hour is over-staffed for its trade */
  txPerHourThreshold: number;
  /** guard: never give up more than this % of the outlet's total sales */
  maxSalesLossPct: number;
  /** average concurrent staff per open hour under the current basis */
  baselineHeadcount: number;
  /** employed-only £ per staff-hour (0 for an all-volunteer roster) */
  ratePerStaffHour: number;
  /** the floor while the door is open; default 1 (but never above baseline) */
  minStaff?: number;
}

export interface LeanStaffingHour {
  slot: number;
  /** season-total transactions in this hour */
  transactions: number;
  /** transactions per open hour (transactions / days traded in this hour) */
  txPerHour: number;
  /** days that traded in this hour across the season — the hours multiplier */
  dayCount: number;
  net: number;
  cogs: number;
  baselineHead: number;
  /** lean headcount; 0 when the hour is trimmed shut */
  recommendedHead: number;
  /** false when trimmed at the day edge */
  open: boolean;
  baselineCost: number;
  recommendedCost: number;
  /** baselineCost - recommendedCost */
  saving: number;
}

export interface LeanStaffingResult {
  hours: LeanStaffingHour[];
  /** first / last still-open slot (minutes from midnight); null if nothing open */
  openFrom: number | null;
  openTo: number | null;
  baselineStaffCost: number;
  recommendedStaffCost: number;
  /** wage saved: baselineStaffCost - recommendedStaffCost */
  laborSaving: number;
  baselineHours: number;
  recommendedHours: number;
  /** net sales given up by the trimmed edge hours (worst case — all lost) */
  salesLost: number;
  salesLostPct: number | null;
  /** gross margin (net - cogs) given up by the trimmed hours */
  marginLost: number;
  /** the headline: laborSaving - marginLost */
  netSaving: number;
  baselineHeadcount: number;
  ratePerStaffHour: number;
  totalSales: number;
  closedHours: number;
}

/**
 * Compute a lean staffing plan for one outlet from its season slot roll-up.
 * `slots` is `outlet.slots` (season roll-up by hour of day). All arithmetic is
 * pure so the panel can recompute live as the slider moves.
 */
export function leanStaffing(slots: SlotBucket[], input: LeanStaffingInput): LeanStaffingResult {
  const minStaff = input.minStaff ?? 1;
  const rate = input.ratePerStaffHour;
  const base = input.baselineHeadcount;
  const threshold = input.txPerHourThreshold;
  // The floor can never push staff ABOVE a roster that already runs under one
  // person on average, so cap it at the baseline.
  const floor = Math.min(minStaff, base);

  const traded = slots
    .filter((s) => s.dayCount > 0)
    .sort((a, b) => a.slot - b.slot)
    .map((s) => {
      const txPerHour = s.dayCount > 0 ? s.transactions / s.dayCount : 0;
      // Proportional cut: below the line, keep the fraction of staff the trade
      // justifies; at or above it, keep the full roster. Clamped to [floor, base].
      const ratio = threshold > 0 ? Math.min(1, txPerHour / threshold) : 1;
      const recommendedHead = Math.max(floor, Math.min(base, base * ratio));
      const baselineCost = base * s.dayCount * rate;
      const recommendedCost = recommendedHead * s.dayCount * rate;
      const h: LeanStaffingHour = {
        slot: s.slot,
        transactions: s.transactions,
        txPerHour,
        dayCount: s.dayCount,
        net: s.net,
        cogs: s.cogs,
        baselineHead: base,
        recommendedHead,
        open: true,
        baselineCost,
        recommendedCost,
        saving: baselineCost - recommendedCost,
      };
      return h;
    });

  const totalSales = traded.reduce((a, h) => a + h.net, 0);
  const salesBudget = (totalSales * Math.max(0, input.maxSalesLossPct)) / 100;

  // ---- edge trim: close the quiet opening / closing hours one at a time ----
  // Only the current outermost open hours are candidates (open later / close
  // earlier), matching the opening-times optimiser's contiguous window.
  let salesLost = 0;
  // guard against runaway loops; there are only ~10-24 slots anyway
  for (let guard = 0; guard < traded.length; guard++) {
    const openIdx = traded.map((h, i) => (h.open ? i : -1)).filter((i) => i >= 0);
    if (openIdx.length <= 1) break;
    const leftI = openIdx[0];
    const rightI = openIdx[openIdx.length - 1];

    const closable = (h: LeanStaffingHour): boolean => {
      if (salesLost + h.net > salesBudget) return false; // sales guard
      // Marginal test: shut only when the lean wage for the hour exceeds the
      // gross margin it earns (net - cogs). With a £0 rate (volunteers) the wage
      // is 0, so no hour is ever worth closing — correct: closing saves nothing.
      return h.recommendedCost > h.net - h.cogs;
    };

    const left = traded[leftI];
    const right = traded[rightI];
    // gain = wage saved by shutting the hour, less the gross margin given up;
    // -Infinity marks an edge that fails the marginal or sales-guard test.
    const leftGain = closable(left) ? left.recommendedCost - (left.net - left.cogs) : -Infinity;
    const rightGain = closable(right) ? right.recommendedCost - (right.net - right.cogs) : -Infinity;
    if (!Number.isFinite(leftGain) && !Number.isFinite(rightGain)) break;

    const pick = leftGain >= rightGain ? left : right;
    pick.open = false;
    pick.recommendedHead = 0;
    pick.recommendedCost = 0;
    pick.saving = pick.baselineCost; // full staffing saved on a shut hour
    salesLost += pick.net;
  }

  const openHours = traded.filter((h) => h.open);
  const closedHours = traded.filter((h) => !h.open);

  const baselineStaffCost = traded.reduce((a, h) => a + h.baselineCost, 0);
  const recommendedStaffCost = traded.reduce((a, h) => a + h.recommendedCost, 0);
  const baselineHours = traded.reduce((a, h) => a + h.baselineHead * h.dayCount, 0);
  const recommendedHours = traded.reduce((a, h) => a + h.recommendedHead * h.dayCount, 0);
  const marginLost = closedHours.reduce((a, h) => a + (h.net - h.cogs), 0);
  const laborSaving = baselineStaffCost - recommendedStaffCost;

  return {
    hours: traded,
    openFrom: openHours.length ? openHours[0].slot : null,
    openTo: openHours.length ? openHours[openHours.length - 1].slot : null,
    baselineStaffCost,
    recommendedStaffCost,
    laborSaving,
    baselineHours,
    recommendedHours,
    salesLost,
    salesLostPct: totalSales > 0 ? (salesLost / totalSales) * 100 : null,
    marginLost,
    netSaving: laborSaving - marginLost,
    baselineHeadcount: base,
    ratePerStaffHour: rate,
    totalSales,
    closedHours: closedHours.length,
  };
}

// ---------------------------------------------------------------------------
//  Formatting
// ---------------------------------------------------------------------------

export function money(n: number): string {
  const r = Math.round(n);
  return (r < 0 ? "-£" : "£") + Math.abs(r).toLocaleString("en-GB");
}

export function moneyPence(n: number): string {
  return (
    (n < 0 ? "-£" : "£") +
    Math.abs(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  );
}

export function pct(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return "--";
  return `${n >= 0 ? "" : "-"}${Math.abs(n).toFixed(1)}%`;
}

export function marginPct(b: { net: number; profit: number }): number | null {
  return b.net > 0 ? (b.profit / b.net) * 100 : null;
}

/**
 * RAG for rostered labour against a period's gross margin — the colour the
 * staffing overlay puts on each month/day card. Employed-only staff cost is the
 * input, so an all-volunteer roster (cost 0) is always "ok" (no cash at risk).
 *   ok    — labour is at most half the gross margin
 *   tight — labour is more than half but still within the margin
 *   over  — labour exceeds the gross margin (or there is no margin to cover it)
 */
export type StaffingRag = "ok" | "tight" | "over";
export function staffingRag(staffCost: number, grossMargin: number): StaffingRag {
  if (!(staffCost > 0)) return "ok";
  if (!(grossMargin > 0)) return "over";
  const ratio = staffCost / grossMargin;
  if (ratio <= 0.5) return "ok";
  if (ratio <= 1) return "tight";
  return "over";
}
